import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../api/client";
import {
  PageTitle, Spinner, Empty, DataCard, Icon, Input, Select, Button, cx, useFlashOnChange
} from "../../components/ui";
import Linimasa from "./trainerSchedule/Linimasa";
import PanelTrainer from "./trainerSchedule/PanelTrainer";
import {
  HARI_KERJA, HARI_SINGKAT, NAMA_BULAN, URUTAN, hariTeks, hitungStatus, periode, rentangTeks,
  saring, statusTrainer, urutkan
} from "./trainerSchedule/logika";

// ─── Trainer Schedule — jam mengajar & jam kosong tiap trainer (Management) ──
//
// Baca saja, lintas semua cabang. Pertanyaan yang paling sering dibawa ke sini: "ada
// trainer yang izin / ada kelas baru jam sekian — siapa yang jamnya kosong?" Karena itu
// pencari jam kosong ada paling atas, dan linimasa di bawahnya ikut berubah begitu sebuah
// jam dipilih. Semua hitungan berasal dari server (trainerschedule.controller), termasuk
// bentrok jam; halaman ini hanya menyaring, mengurutkan, dan menggambar.
//
// Jadwal = susunan kelas SEKARANG di Manajemen Kelas. Pemilih bulan hanya mengubah angka
// bulanan (mengajar, diganti, mengganti). "Hari ini" dari server (jam Jakarta).

const SUBJUDUL = "Lihat jam mengajar setiap trainer. Kalau ada trainer yang izin atau ada kelas baru, pilih jamnya di bawah untuk mencari trainer yang sedang tidak mengajar di jam itu.";
const CHIP = "inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-bold text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300";
const CHIP_KUNING = "inline-flex items-center gap-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-xs font-bold text-amber-700 dark:text-amber-300";
const PILIHAN = "lift-press rounded-lg border px-3 py-1.5 text-sm font-bold";
const AKTIF = "border-primary bg-primary text-white";
const BIASA = "border-slate-200 bg-white text-slate-600 hover:border-primary/60 hover:text-primary dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300";

export default function TrainerSchedulePage({ setToast }) {
  const [data, setData] = useState(null);
  const [memuat, setMemuat] = useState(true);
  const [galat, setGalat] = useState("");
  const [minta, setMinta] = useState(null);     // { year, month }; null = bulan berjalan menurut server
  const [ulang, setUlang] = useState(0);
  const [arah, setArah] = useState("kanan");
  const [jam, setJam] = useState("");           // label tombol jam; "" = semua jam
  const [hari, setHari] = useState([]);         // hari terpilih; [] = Senin–Sabtu
  const [hanyaKosong, setHanyaKosong] = useState(false);
  const [cari, setCari] = useState("");
  const [cabang, setCabang] = useState("");
  const [urutan, setUrutan] = useState("nama");
  const [dipilih, setDipilih] = useState(null);
  const nomor = useRef(0);

  useEffect(() => {
    // Klik bulan beruntun: hanya jawaban permintaan TERAKHIR yang dipakai.
    const ini = ++nomor.current;
    setMemuat(true);
    api.getTrainerSchedule(minta || {})
      .then((body) => {
        if (ini !== nomor.current) return;
        setData(body);
        setGalat("");
      })
      .catch((err) => {
        if (ini !== nomor.current) return;
        const pesan = err.message || "Gagal memuat Trainer Schedule";
        setGalat(pesan);
        if (setToast) setToast({ type: "error", message: pesan });
      })
      .finally(() => { if (ini === nomor.current) setMemuat(false); });
  }, [minta, ulang, setToast]);

  const S = useMemo(() => (data && jam ? data.sesi.find((s) => s.sesi === jam) || null : null), [data, jam]);
  const dasar = useMemo(() => (data ? saring(data.trainer, { cari, cabang }) : []), [data, cari, cabang]);
  // Angka di tombol jam mengikuti hari & penyaring yang dipilih — bukan "Hanya yang kosong".
  const hitungan = useMemo(
    () => new Map((data ? data.sesi : []).map((s) => [s.sesi, hitungStatus(dasar, s.sesi, hari)])),
    [data, dasar, hari]
  );
  const tampil = useMemo(() => {
    const urut = urutkan(dasar, urutan, S ? S.sesi : "", hari);
    return S && hanyaKosong ? urut.filter((t) => statusTrainer(t, S.sesi, hari) === "kosong") : urut;
  }, [dasar, urutan, S, hari, hanyaKosong]);

  const tutupPanel = useCallback(() => setDipilih(null), []);
  const tPanel = data && dipilih != null ? data.trainer.find((t) => t.id === dipilih) || null : null;

  const [tahunIni, bulanIniAngka] = data ? data.hariIni.split("-").map(Number) : [0, 0];
  const tampilBulan = minta || (data ? { year: data.tahun, month: data.bulan } : null);
  const bisaMaju = !!tampilBulan && !!data
    && tampilBulan.year * 12 + tampilBulan.month < tahunIni * 12 + bulanIniAngka;
  const geserBulan = (n) => {
    const t = new Date(tampilBulan.year, tampilBulan.month - 1 + n, 1);
    setArah(n < 0 ? "kiri" : "kanan");
    setMinta({ year: t.getFullYear(), month: t.getMonth() + 1 });
  };
  const bulanBerjalan = !!data && data.tahun === tahunIni && data.bulan === bulanIniAngka;
  const namaBulanData = data ? `${NAMA_BULAN[data.bulan - 1]} ${data.tahun}` : "";
  const tukarHari = (d) => setHari((h) => (h.includes(d) ? h.filter((x) => x !== d) : [...h, d].sort((a, b) => a - b)));

  const r = data && data.ringkas;
  const subjudul = (
    <>
      {SUBJUDUL}
      {r && (
        <span className="mt-3 flex flex-wrap gap-2">
          <span className={CHIP}><Icon name="group" className="text-sm" />{r.trainer} trainer</span>
          <span className={CHIP}><Icon name="school" className="text-sm" />{r.kelasAktif} kelas</span>
          <span className={CHIP}>
            <Icon name="swap_horiz" className="text-sm" />
            {bulanBerjalan ? "Bulan ini" : `${namaBulanData}:`} {r.penggantianBulanIni} kali kelas diganti trainer lain
          </span>
          {r.kelasBelumBerhari > 0 && (
            <span className={CHIP_KUNING}>
              <Icon name="edit_calendar" className="text-sm" />
              {r.kelasBelumBerhari} kelas belum diisi hari belajarnya
            </span>
          )}
        </span>
      )}
    </>
  );

  return (
    <div>
      <PageTitle
        title="Trainer Schedule"
        subtitle={subjudul}
        action={tampilBulan && (
          <PemilihBulan
            tahun={tampilBulan.year}
            bulan={tampilBulan.month}
            arah={arah}
            memuat={memuat && !!data}
            bisaMaju={bisaMaju}
            onGeser={geserBulan}
          />
        )}
      />

      {!data ? (
        memuat ? (
          <Spinner label="Memuat jadwal trainer…" />
        ) : (
          <div className="anim-fade rounded-xl border border-dashed border-slate-300 p-10 text-center text-slate-500 dark:border-slate-700">
            <p>{galat || "Trainer Schedule belum bisa dimuat."}</p>
            <Button variant="secondary" className="mt-4" onClick={() => setUlang((n) => n + 1)}>
              <Icon name="refresh" />
              Coba lagi
            </Button>
          </div>
        )
      ) : (
        <>
          <DataCard delay={1} className="mb-6 p-5">
            <div className="flex items-center gap-2">
              <Icon name="event_available" className="text-primary" />
              <h2 className="text-lg font-black">Cari trainer yang jamnya kosong</h2>
            </div>

            {!data.sesi.length ? (
              <p className="mt-3 text-sm text-slate-500">
                Belum ada kelas aktif yang jamnya terbaca, jadi belum ada jam untuk dipilih.
              </p>
            ) : (
              <>
                <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label="Pilih jam">
                  <button
                    type="button"
                    aria-pressed={!S}
                    onClick={() => setJam("")}
                    className={cx("lift-press rounded-xl border px-4 text-sm font-black", !S ? AKTIF : BIASA)}
                  >
                    Semua jam
                  </button>
                  {data.sesi.map((s) => (
                    <TombolJam
                      key={s.sesi}
                      s={s}
                      aktif={!!S && S.sesi === s.sesi}
                      n={hitungan.get(s.sesi)}
                      onClick={() => setJam(S && S.sesi === s.sesi ? "" : s.sesi)}
                    />
                  ))}
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-2" role="group" aria-label="Pilih hari">
                  <span className="mr-1 text-xs font-bold uppercase tracking-wide text-slate-500">Hari</span>
                  <button type="button" aria-pressed={!hari.length} onClick={() => setHari([])}
                    className={cx(PILIHAN, !hari.length ? AKTIF : BIASA)}>
                    Semua
                  </button>
                  {HARI_KERJA.map((d) => (
                    <button key={d} type="button" aria-pressed={hari.includes(d)} onClick={() => tukarHari(d)}
                      className={cx(PILIHAN, hari.includes(d) ? AKTIF : BIASA)}>
                      {HARI_SINGKAT[d]}
                    </button>
                  ))}
                </div>

                {S ? (
                  <Kalimat S={S} hari={hari} n={hitungan.get(S.sesi)} hanyaKosong={hanyaKosong} setHanyaKosong={setHanyaKosong} />
                ) : (
                  <p className="mt-4 text-sm text-slate-500">
                    Pilih jam di atas untuk melihat siapa yang sedang tidak mengajar. Pilih harinya juga
                    supaya hasilnya lebih tepat.
                  </p>
                )}
              </>
            )}
          </DataCard>

          <div className="anim-rise anim-d2 mb-4 flex flex-col gap-3 md:flex-row md:items-center">
            <div className="relative md:w-72">
              <Icon name="search" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-lg text-slate-400" />
              <Input
                value={cari}
                onChange={(e) => setCari(e.target.value)}
                placeholder="Cari nama atau kode trainer"
                aria-label="Cari nama atau kode trainer"
                className="pl-10"
              />
            </div>
            <Select value={cabang} onChange={(e) => setCabang(e.target.value)} aria-label="Cabang" className="md:w-56">
              <option value="">Semua cabang</option>
              {data.cabang.map((b) => (
                <option key={b.id} value={b.code}>{b.code}{b.name ? ` · ${b.name}` : ""}</option>
              ))}
            </Select>
            <Select value={urutan} onChange={(e) => setUrutan(e.target.value)} aria-label="Urutkan" className="md:w-72">
              {URUTAN.map(([nilai, label]) => <option key={nilai} value={nilai}>{label}</option>)}
            </Select>
            <p className="text-sm text-slate-500 md:ml-auto">
              Menampilkan <b>{tampil.length}</b> dari <b>{data.trainer.length}</b> trainer
            </p>
          </div>

          <DataCard delay={3}>
            {tampil.length ? (
              <Linimasa
                trainers={tampil}
                sesi={S}
                hari={hari}
                onPilih={setDipilih}
                labelBulan={bulanBerjalan ? "Bulan ini" : namaBulanData}
              />
            ) : (
              <div className="p-6">
                <Empty
                  label={!data.trainer.length
                    ? "Belum ada akun trainer. Trainer = akun dengan role atau posisi trainer di User Management."
                    : S && hanyaKosong && dasar.length
                    ? `Tidak ada trainer yang jamnya kosong di jam ${rentangTeks(S.mulai, S.selesai)} pada ${hariTeks(hari)}.`
                    : "Tidak ada trainer yang cocok dengan pencarian atau cabang ini."}
                />
              </div>
            )}
          </DataCard>

          <Legenda />

          <PanelTrainer
            trainer={tPanel}
            buka={!!tPanel}
            onTutup={tutupPanel}
            sesi={S}
            hari={hari}
            data={data}
            labelBulan={namaBulanData}
            bulanBerjalan={bulanBerjalan}
          />
        </>
      )}
    </div>
  );
}

function PemilihBulan({ tahun, bulan, arah, memuat, bisaMaju, onGeser }) {
  const tombol = "rounded-lg p-2 text-slate-600 transition-colors hover:bg-slate-100 hover:text-primary disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-slate-600 dark:text-slate-300 dark:hover:bg-slate-800";
  return (
    <div className="flex items-center gap-1 self-start rounded-xl border border-slate-200 bg-white p-1 shadow-sm dark:border-slate-700 dark:bg-slate-900 md:self-auto">
      <button type="button" onClick={() => onGeser(-1)} aria-label="Bulan sebelumnya" className={tombol}>
        <Icon name="chevron_left" />
      </button>
      <span className="flex w-40 items-center justify-center gap-1.5 overflow-hidden text-sm font-black">
        {/* Ber-key: tiap bulan berganti labelnya masuk lagi, dari arah panah yang ditekan. */}
        <span key={`${tahun}-${bulan}`} aria-live="polite" className={arah === "kiri" ? "anim-slide-l" : "anim-slide-r"}>
          {NAMA_BULAN[bulan - 1]} {tahun}
        </span>
        {memuat && (
          <span aria-hidden="true"><Icon name="progress_activity" className="animate-spin text-base text-primary" /></span>
        )}
      </span>
      <button type="button" onClick={() => onGeser(1)} disabled={!bisaMaju} aria-label="Bulan berikutnya" className={tombol}>
        <Icon name="chevron_right" />
      </button>
    </div>
  );
}

function TombolJam({ s, aktif, n, onClick }) {
  const kilat = useFlashOnChange(n.kosong);
  return (
    <button
      type="button"
      aria-pressed={aktif}
      onClick={onClick}
      className={cx(
        "lift-press flex min-w-[8.75rem] flex-col items-start rounded-xl border px-3 py-2 text-left",
        aktif
          ? "border-primary bg-primary text-white shadow-lg shadow-primary/25"
          : "border-slate-200 bg-white text-slate-900 hover:border-primary/60 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
      )}
    >
      <span className={cx("text-[10px] font-black uppercase tracking-wider", aktif ? "text-white/75" : "text-slate-400")}>
        {periode(s.mulai)}
      </span>
      <span className="text-sm font-black tabular-nums">{rentangTeks(s.mulai, s.selesai)}</span>
      <span className={cx("mt-0.5 text-xs", aktif ? "text-white/90" : "text-slate-500")}>
        <b className={cx("px-0.5", kilat && "anim-flash", aktif ? "text-white" : "text-primary dark:text-[#3987e5]")}>
          {n.kosong}
        </b>{" "}
        kosong · {n["ada-kelas"]} ada kelas
      </span>
    </button>
  );
}

function Kalimat({ S, hari, n, hanyaKosong, setHanyaKosong }) {
  return (
    <div className="anim-fade mt-4 flex flex-col gap-3 rounded-xl bg-slate-50 p-3 dark:bg-slate-800/40 md:flex-row md:items-center md:justify-between">
      <p className="text-sm text-slate-600 dark:text-slate-300" aria-live="polite">
        Jam <b>{rentangTeks(S.mulai, S.selesai)}</b>, {hariTeks(hari)}:{" "}
        <b className="text-primary dark:text-[#3987e5]">{n.kosong} trainer jamnya kosong</b>
        {n["belum-pasti"] > 0 && `, ${n["belum-pasti"]} belum pasti`}, {n["ada-kelas"]} sedang ada kelas
      </p>
      <div role="group" aria-label="Tampilkan" className="inline-flex shrink-0 self-start rounded-lg border border-slate-200 bg-white p-0.5 dark:border-slate-700 dark:bg-slate-900 md:self-auto">
        {[[false, "Semua trainer"], [true, "Hanya yang jamnya kosong"]].map(([nilai, label]) => (
          <button
            key={label}
            type="button"
            aria-pressed={hanyaKosong === nilai}
            onClick={() => setHanyaKosong(nilai)}
            className={cx(
              "rounded-md px-3 py-1.5 text-xs font-bold transition-colors",
              hanyaKosong === nilai ? "bg-primary text-white" : "text-slate-600 hover:text-primary dark:text-slate-300"
            )}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

function Contoh({ className, children }) {
  return (
    <span className="inline-flex items-center gap-2">
      <span aria-hidden="true" className={cx("inline-block", className)} />
      {children}
    </span>
  );
}

function Legenda() {
  return (
    <DataCard delay={4} className="mt-6 p-5">
      <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-slate-600 dark:text-slate-300">
        <Contoh className="h-3.5 w-7 rounded bg-primary dark:bg-[#2563eb]">Jadwal mengajar</Contoh>
        <Contoh className="h-3.5 w-7 rounded border border-amber-500/60 bg-amber-500/15">Hari belum diisi ("hari?")</Contoh>
        <Contoh className="h-3.5 w-7 rounded bg-[#dbe2ea] dark:bg-[#2a3547]">Jadwal, saat sebuah jam dipilih</Contoh>
        <Contoh className="h-3.5 w-7 rounded border-2 border-primary bg-white dark:border-[#3987e5] dark:bg-slate-900">Jam kosong</Contoh>
        <Contoh className="h-2.5 w-2.5 rounded-full bg-[#eb6834] dark:bg-[#d95926]">Kelasnya diganti</Contoh>
        <Contoh className="h-2.5 w-2.5 rounded-full border-2 border-[#0d59f2] dark:border-[#3987e5]">Mengganti trainer lain</Contoh>
      </div>
      <dl className="mt-4 grid gap-4 text-sm md:grid-cols-3">
        <div>
          <dt className="font-black text-slate-900 dark:text-white">Jam kosong</dt>
          <dd className="mt-1 text-slate-500">Trainer tidak punya kelas di jam itu pada hari yang dipilih. Jam yang bertumpuk dianggap tidak bisa.</dd>
        </div>
        <div>
          <dt className="font-black text-slate-900 dark:text-white">Belum pasti</dt>
          <dd className="mt-1 text-slate-500">Punya kelas di jam itu, tapi admin cabang belum mengisi harinya.</dd>
        </div>
        <div>
          <dt className="font-black text-slate-900 dark:text-white">Kelas diganti</dt>
          <dd className="mt-1 text-slate-500">Kelasnya diajar trainer lain. Alasannya belum dicatat terpisah.</dd>
        </div>
      </dl>
      <p className="mt-4 text-xs text-slate-400">
        Jadwal mengikuti susunan kelas saat ini di Manajemen Kelas. Pemilih bulan hanya mengubah
        angka bulanan: berapa kali mengajar, diganti, dan mengganti.
      </p>
    </DataCard>
  );
}
